// Token storage — uses Capacitor Preferences on native, localStorage on web.

import { isNative } from "./platform";

const KEY = "myams.token";

/**
 * Synchronous best-effort check. On web we read localStorage directly so
 * the router can decide where to send the user on first paint. On native,
 * tokens live in Capacitor Preferences (async), so we return null and let
 * the async path resolve.
 */
export function hasTokenSync(): boolean {
  if (typeof window === "undefined") return false;
  if (isNative()) return false;
  try { return localStorage.getItem(KEY) !== null; } catch { return false; }
}

async function nativePrefs(): Promise<{ get: (k: string) => Promise<string | null>; set: (k: string, v: string) => Promise<void>; remove: (k: string) => Promise<void> } | null> {
  if (!isNative()) return null;
  try {
    const mod = await import("@capacitor/preferences");
    return {
      get: async (k) => (await mod.Preferences.get({ key: k })).value,
      set: async (k, v) => { await mod.Preferences.set({ key: k, value: v }); },
      remove: async (k) => { await mod.Preferences.remove({ key: k }); },
    };
  } catch {
    return null;
  }
}

export async function getToken(): Promise<string | null> {
  const p = await nativePrefs();
  if (p) return p.get(KEY);
  try { return localStorage.getItem(KEY); } catch { return null; }
}

export async function setToken(token: string): Promise<void> {
  const p = await nativePrefs();
  if (p) return p.set(KEY, token);
  try { localStorage.setItem(KEY, token); } catch { /* ignore */ }
}

export async function clearToken(): Promise<void> {
  const p = await nativePrefs();
  if (p) return p.remove(KEY);
  try { localStorage.removeItem(KEY); } catch { /* ignore */ }
}
